/**
 * 邀请链接（经销商名片上的二维码）参数解析与清理。
 *
 * ★关键坑（2026-10-20 实测）：平台用的是 **hash 路由**（`new VueRouter({ mode: 'hash' })`），
 *   `https://www.csmf.ltd?invite=CODE` 里的 `?invite=` 落在 **`window.location.search`**
 *   （hash 之前），**根本进不了 `$route.query`** —— 只读 `$route.query` 的话，
 *   名片上的短链接就是死的（用户扫了也没反应）。
 *
 * 所以这里两处都读：
 *   ① `window.location.search`（hash 模式下的主力，名片就用这个）
 *   ② hash 里的 query（`#/home?invite=CODE` 这种手写/站内跳转）
 *   ③ 最后兜 `route.query`（history 模式或路由自己带进来的）
 */

/** 从地址里挖邀请码；没有返回空串 */
export function inviteCodeFromLocation(route) {
  const norm = v => String(v || '').trim()

  // ① 原生 search 串（hash 之前的 ?invite=）
  try {
    const s = new URLSearchParams(window.location.search || '')
    const v = norm(s.get('invite') || s.get('invite_code'))
    if (v) {
      return v
    }
  } catch (e) {
    // 老浏览器没有 URLSearchParams：落到下面两种读法
  }

  // ② hash 里的 query（有些场景会把参数写在 # 后面）
  try {
    const hash = window.location.hash || ''
    const qi = hash.indexOf('?')
    if (qi >= 0) {
      const hs = new URLSearchParams(hash.slice(qi + 1))
      const v = norm(hs.get('invite') || hs.get('invite_code'))
      if (v) {
        return v
      }
    }
  } catch (e) {
    // 忽略
  }

  // ③ 路由 query 兜底
  const q = (route && route.query) || {}
  return norm(q.invite || q.invite_code)
}

/**
 * 把邀请码参数从地址上摘掉（search 与 hash 两处都清）。
 *
 * 为什么必须清：不清的话，登录成功后 redirect 回同一路径会再弹一次注册框，
 * 用户刷新页面也会反复弹 —— 而邀请码这时已经落到 store 的注册表单里了。
 *
 * @param router vue-router 实例（清 hash 里的 query 要走它，保持内部状态一致）
 * @param route  当前路由
 */
export function clearInviteFromLocation(router, route) {
  // search 上的：直接改历史记录，不影响 router 状态（hash 没动）
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has('invite') || url.searchParams.has('invite_code')) {
      url.searchParams.delete('invite')
      url.searchParams.delete('invite_code')
      window.history.replaceState(null, '', url.toString())
    }
  } catch (e) {
    // 解析失败就留着参数，功能不受影响
  }

  // hash 上的 query：走 router 换掉，避免路由状态和地址不一致
  const q = (route && route.query) || {}
  if (q.invite || q.invite_code) {
    const rest = { ...q }
    delete rest.invite
    delete rest.invite_code
    if (router && route) {
      router.replace({ path: route.path, query: rest }).catch(() => {})
    }
  }
}
